import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";
import CompanySettings from "../Companies/CompanySettings";

// Replaces the free-sms.me API key that every WhatsApp message goes out with.
//
// Login codes, order confirmations and balance warnings all travel over
// WhatsApp, and when the key expires they all fail quietly. This page lets an
// admin paste a new key and send themselves a test message to prove it works,
// without anyone opening the database. The key is posted in the request body
// and only ever comes back masked.
const WhatsAppApiKey = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [maskedKey, setMaskedKey] = useState("");
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("whatsapp_api_key?operation=get");
      if (res?.data?.status === "success") {
        setConfigured(Boolean(res.data.configured));
        setMaskedKey(res.data.masked_key || "");
      }
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const key = newKey.trim();
    if (!key) return;

    setSaving(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("whatsapp_api_key?operation=update", {
        api_key: key,
      });
      if (res?.data?.status === "success") {
        setConfigured(true);
        setMaskedKey(res.data.masked_key || "");
        setNewKey("");
        setShowKey(false);
        Toast.fire({ title: translate("whatsappKeySaved"), icon: "success" });
      } else {
        Toast.fire({
          title: res?.data?.reason || "Something Went Wrong!",
          icon: "error",
        });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("whatsapp_api_key?operation=test");
      setTestResult({
        ok: res?.data?.status === "success",
        number: res?.data?.number,
        reason: res?.data?.reason,
      });
    } catch (err) {
      setTestResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("whatsappKeyHint")}
      </Typography>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 2 }}>
          <Typography variant="subtitle1">{translate("Current key")}</Typography>
          {loading ? (
            <CircularProgress size={18} />
          ) : configured ? (
            <Chip size="small" color="success" label={maskedKey} sx={{ fontFamily: "monospace" }} />
          ) : (
            <Chip size="small" color="warning" label={translate("whatsappKeyMissing")} />
          )}
        </Box>

        <Box
          component="form"
          onSubmit={save}
          sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}
        >
          <TextField
            size="small"
            type={showKey ? "text" : "password"}
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            label={translate("New API key")}
            autoComplete="off"
            sx={{ minWidth: 320, flexGrow: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowKey((v) => !v)}
                    aria-label="toggle key visibility"
                    edge="end"
                  >
                    {showKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" disabled={saving || !newKey.trim()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : translate("Save")}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          {translate("Test message")}
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("whatsappTestHint")}
        </Typography>
        <Button variant="outlined" onClick={sendTest} disabled={testing || !configured}>
          {testing ? <CircularProgress size={20} color="inherit" /> : translate("Send test message")}
        </Button>

        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} sx={{ marginTop: 2 }}>
            {testResult.number ? `${testResult.number}: ` : ""}
            {testResult.ok ? translate("whatsappTestSent") : testResult.reason}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

const WhatsAppSettings = ({ isDarktheme }) => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ padding: "16px 24px 0", color: isDarktheme ? "#fff" : "inherit" }}>
        WhatsApp
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="whatsapp settings tabs">
          <Tab className={!isDarktheme ? "" : "text-light"} label={translate("API key")} />
          <Tab className={!isDarktheme ? "" : "text-light"} label={translate("All settings")} />
        </Tabs>
      </Box>
      {tab === 0 && <WhatsAppApiKey isDarktheme={isDarktheme} />}
      {tab === 1 && <CompanySettings companyId="whatsapp" />}
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(WhatsAppSettings);
